import { UrlsBox as UrlsBoxConfig } from '@lemontaps/lib/types/api';
import ContentBoxUi from '../../components/ContentBoxUI';
import { Box, Stack } from '@mui/material';
import UrlButtonItem from './components/UrlButtonItem';
import VideoItem from './components/VideoItem';
import NoteIcon from '@mui/icons-material/Note';
import { ProfileEditProps, TrackHandlers } from '../../types';
import AddLinkButton from './components/AddLinkButton';
import AddFilePopup from './components/AddFilePopup';
import LinksEdit from './components/LinksEdit';
import FilesEdit from './components/FilesEdit';
import { assertUnreachable } from '@lemontaps/lib/types/util';
import { customCssClasses } from '@lemontaps/lib/constants';

// TODO: Consolidate types
type Props<T extends UrlsBoxConfig['name']> = {
  config: UrlsBoxConfig;
  editProps?: T extends 'urlsBox_links'
    ? ProfileEditProps['linksBoxEditProps']
    : T extends 'urlsBox_files'
      ? ProfileEditProps['filesBoxEditProps']
      : never;
  trackHandler: T extends 'urlsBox_links'
    ? TrackHandlers['linkClickTrackHandler']
    : T extends 'urlsBox_files'
      ? TrackHandlers['fileClickTrackHandler']
      : never;
  containerClassName?: string;
  titleClassName?: string;
};

export default function UrlsBox<T extends UrlsBoxConfig['name']>({
  config,
  editProps,
  trackHandler,
  containerClassName,
  titleClassName,
}: Props<T>) {
  const isEdit = !!editProps;
  return (
    <ContentBoxUi
      containerClassName={containerClassName}
      titleClassName={titleClassName}
      title={
        <Box display='flex' alignItems='center'>
          {config.label}
          {editProps?.labelAction && editProps.labelAction}
        </Box>
      }
      actionButton={
        editProps ? (
          config.name === 'urlsBox_links' ? (
            // TODO: get rid of typecast
            <AddLinkButton {...(editProps as ProfileEditProps['linksBoxEditProps'])} />
          ) : config.name === 'urlsBox_files' ? (
            <AddFilePopup {...(editProps as ProfileEditProps['filesBoxEditProps'])} />
          ) : (
            assertUnreachable(config.name)
          )
        ) : undefined
      }
      isEdit={isEdit}
    >
      {editProps && editProps.editRightAllowed ? (
        config.name === 'urlsBox_links' ? (
          <LinksEdit
            links={(editProps as ProfileEditProps['linksBoxEditProps']).links}
            standardLinksAfter={
              (editProps as ProfileEditProps['linksBoxEditProps']).standardLinksAfter
            }
            standardLinksBefore={
              (editProps as ProfileEditProps['linksBoxEditProps']).standardLinksBefore
            }
            genericWebsiteColor={
              (editProps as ProfileEditProps['linksBoxEditProps']).genericWebsiteColor
            }
            defaultLinkId={(editProps as ProfileEditProps['linksBoxEditProps']).defaultLinkId}
            onReorder={(editProps as ProfileEditProps['linksBoxEditProps']).onReorder}
            onToggleDisplay={(editProps as ProfileEditProps['linksBoxEditProps']).onToggleDisplay}
            onToggleDefault={(editProps as ProfileEditProps['linksBoxEditProps']).onToggleDefault}
            onEdit={(editProps as ProfileEditProps['linksBoxEditProps']).onEdit}
            onDelete={(editProps as ProfileEditProps['linksBoxEditProps']).onDelete}
          />
        ) : (
          <FilesEdit
            files={(editProps as ProfileEditProps['filesBoxEditProps']).files}
            onReorder={(editProps as ProfileEditProps['filesBoxEditProps']).onReorder}
            onDelete={(editProps as ProfileEditProps['filesBoxEditProps']).onDelete}
            onToggleDisplay={(editProps as ProfileEditProps['filesBoxEditProps']).onToggleDisplay}
            standardFilesBefore={
              (editProps as ProfileEditProps['filesBoxEditProps']).standardFilesBefore
            }
            standardFilesAfter={
              (editProps as ProfileEditProps['filesBoxEditProps']).standardFilesAfter
            }
            genericFileBoxColor={
              (editProps as ProfileEditProps['filesBoxEditProps']).genericFileBoxColor
            }
          />
        )
      ) : (
        <Stack
          direction='column'
          spacing={1.6}
          className={
            config.name === 'urlsBox_files'
              ? customCssClasses.filesBox.files.className
              : customCssClasses.linksBox.links.className
          }
        >
          {config.items.map(item =>
            item.renderAs === 'link' ? (
              <UrlButtonItem
                iconClassName={
                  config.name === 'urlsBox_files'
                    ? customCssClasses.filesBox.fileItemIcon.className
                    : customCssClasses.linksBox.linkItemIcon.className
                }
                itemClassName={
                  config.name === 'urlsBox_files'
                    ? customCssClasses.filesBox.fileItem.className
                    : customCssClasses.linksBox.linkItem.className
                }
                textClassName={
                  config.name === 'urlsBox_files'
                    ? customCssClasses.filesBox.fileItemText.className
                    : customCssClasses.linksBox.linkItemText.className
                }
                key={item.url}
                {...item}
                onClick={trackHandler ? () => trackHandler(item.meta) : undefined}
                icon={
                  config.name === 'urlsBox_files' ? (
                    <NoteIcon className={customCssClasses.filesBox.fileItemIcon.className} />
                  ) : undefined
                }
              />
            ) : item.renderAs === 'video' ? (
              <VideoItem {...item} />
            ) : null,
          )}
        </Stack>
      )}
    </ContentBoxUi>
  );
}
